import type { ReactI18n } from './types'
import { createReactI18n } from './react'

/**
 * Configuration for library i18n with default English translations
 */
export type LibI18nConfig<T> = {
  /**
   * Default translations (typically English) that the library ships with.
   * Used as fallback if app doesn't provide translations.
   */
  defaultTranslations: T
}

/**
 * Extended ReactI18n interface with setAppTranslations for library use
 */
export type LibI18n<T> = ReactI18n<T> & {
  /**
   * Redirects library i18n calls to use the app's i18n instance.
   * Call this once at app initialization to forward all library translation
   * requests to the app's i18n system.
   *
   * @param appI18n The app's ReactI18n instance that manages translations
   *
   * @example
   * // In app
   * import { libI18n } from '@my/ui-lib'
   *
   * const appI18n = createReactI18n({
   *   fallbackLocale: 'en',
   *   localStorageKey: 'app-locale',
   *   loadTranslation: (locale) => import(`./translations/${locale}`)
   * })
   *
   * // Forward library i18n to app i18n
   * libI18n.setAppTranslations(appI18n)
   */
  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible ReactI18n due to generic invariance
  setAppTranslations: (appI18n: ReactI18n<any>) => void
  /**
   * The default translations shape that apps can import to understand
   * what translations they need to provide
   */
  defaultTranslations: T
}

/**
 * Creates an i18n instance for libraries with default English translations.
 *
 * Libraries always use English defaults and don't interact with localStorage/navigator.
 * Apps can forward library i18n to their own i18n instance via `setAppTranslations`.
 *
 * @example
 * // In library (@my/ui-lib/i18n.ts)
 * export const uiTranslations = {
 *   ui: { button: { submit: 'Submit', cancel: 'Cancel' } }
 * } as const
 *
 * export const uiI18n = createLibI18n({
 *   defaultTranslations: uiTranslations
 * })
 *
 * // In library component
 * import { uiI18n } from './i18n'
 * const { t } = uiI18n.useTranslation()
 * t('ui.button.submit') // Works with defaults or forwarded to app
 *
 * @example
 * // In app
 * import { uiI18n, uiTranslations } from '@my/ui-lib'
 * // Apps can use typeof uiTranslations to understand required shape
 *
 * const appI18n = createReactI18n({
 *   fallbackLocale: 'en',
 *   localStorageKey: 'app-locale',
 *   loadTranslation: (locale) => import(`./translations/${locale}`)
 * })
 *
 * // Forward library i18n calls to app i18n
 * uiI18n.setAppTranslations(appI18n)
 */
export function createLibI18n<T>(config: LibI18nConfig<T>): LibI18n<T> {
  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible ReactI18n due to generic invariance
  let appI18n: ReactI18n<any> | null = null

  // Create initial i18n with default translations (English only)
  // No localStorage or navigator interaction - libraries always use 'en'
  const defaultI18n = createReactI18n({
    fallbackLocale: 'en',
    localStorageKey: '__lib_i18n_unused__', // Unique key to avoid conflicts
    localStorage: undefined, // Disable localStorage
    loadTranslation: () => Promise.resolve(config.defaultTranslations),
  })

  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible ReactI18n due to generic invariance
  const setAppTranslations = (newAppI18n: ReactI18n<any>): void => {
    appI18n = newAppI18n
  }

  // Wrapper functions that dynamically access getters to avoid capturing values at module load time
  // This ensures that when userland code destructures exports, they get functions that always
  // call through the getter, not a snapshot of the getter's value at import time
  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible prefix due to generic invariance
  const useTranslation: typeof defaultI18n.useTranslation = (prefix?: any) => {
    // oxlint-disable-next-line typescript/unbound-method -- dynamic dispatch through appI18n/defaultI18n, not method refs losing this
    const fn = appI18n?.useTranslation ?? defaultI18n.useTranslation
    return fn(prefix)
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible props due to generic invariance
  const I18nProvider: typeof defaultI18n.I18nProvider = (props: any) => {
    const Component = appI18n?.I18nProvider ?? defaultI18n.I18nProvider
    // oxlint-disable-next-line typescript/no-unsafe-argument -- props typed as any due to generic invariance, safe at runtime
    return Component(props)
  }

  const useLocale = () => {
    const fn = appI18n?.useLocale ?? defaultI18n.useLocale
    return fn()
  }

  const setLocale: typeof defaultI18n.setLocale = (locale: string) => {
    const fn = appI18n?.setLocale ?? defaultI18n.setLocale
    fn(locale)
  }

  const loadTranslations: typeof defaultI18n.loadTranslations = (
    locale?: string,
  ) => {
    const fn = appI18n?.loadTranslations ?? defaultI18n.loadTranslations
    // oxlint-disable-next-line typescript/no-unsafe-return -- fn typed as any due to generic invariance, safe at runtime
    return fn(locale)
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- accepts any structurally compatible params due to generic invariance
  const t: typeof defaultI18n.t = (key: any, params?: any) => {
    const fn = appI18n?.t ?? defaultI18n.t
    // oxlint-disable-next-line typescript/no-unsafe-argument -- params passed through from generic i18n fn signature
    return fn(key, params)
  }

  const getLocale = () => {
    const fn = appI18n?.getLocale ?? defaultI18n.getLocale
    return fn()
  }

  return {
    useTranslation,
    I18nProvider,
    useLocale,
    setLocale,
    loadTranslations,
    t,
    getLocale,
    setAppTranslations,
    defaultTranslations: config.defaultTranslations,
  }
}
